import {
  reconcileProbeSpend,
  type ProbeSpendLeg,
  type ProbeSpendTotals,
  type ProbeSpendVerdict,
} from '@nadoutong/contracts';

export interface ProbeSpendSkuGroup {
  readonly key: string;
  readonly skuId: string | null;
  readonly skuName: string | null;
  readonly legs: readonly ProbeSpendLeg[];
  readonly subtotal: ProbeSpendTotals;
  /** The same count-based reconcile, run on the SKU's summed legs. */
  readonly verdict: ProbeSpendVerdict;
}

function nullableSum(legs: readonly ProbeSpendLeg[], pick: (leg: ProbeSpendLeg) => number | null): number | null {
  let total = 0;
  for (const leg of legs) {
    const value = pick(leg);
    if (value === null) return null;
    total += value;
  }
  return total;
}

export function subtotalOf(legs: readonly ProbeSpendLeg[]): ProbeSpendTotals {
  const sum = (pick: (leg: ProbeSpendLeg) => number): number =>
    legs.reduce((total, leg) => total + pick(leg), 0);
  return {
    shots: sum((leg) => leg.shots),
    input_tokens: sum((leg) => leg.input_tokens),
    output_tokens: sum((leg) => leg.output_tokens),
    logged_cost_usd_micros: sum((leg) => leg.logged_cost_usd_micros),
    chargeable_cost_usd_micros: sum((leg) => leg.chargeable_cost_usd_micros),
    procurement_cost_usd_micros: sum((leg) => leg.procurement_cost_usd_micros),
    chargeable_shots: sum((leg) => leg.chargeable_shots),
    unpriced_shots: sum((leg) => leg.unpriced_shots),
    pre_fix_shots: sum((leg) => leg.pre_fix_shots),
    charges: nullableSum(legs, (leg) => leg.charges),
    charged_usd_micros: nullableSum(legs, (leg) => leg.charged_usd_micros),
    allocated_charges: nullableSum(legs, (leg) => leg.allocated_charges),
    allocated_cny_micros: nullableSum(legs, (leg) => leg.allocated_cny_micros),
    overflow_charges: nullableSum(legs, (leg) => leg.overflow_charges),
  };
}

/** Verdict for a sum of legs; a mismatching leg keeps the group red. */
export function totalsVerdict(totals: ProbeSpendTotals, legs: readonly ProbeSpendLeg[]): ProbeSpendVerdict {
  const summed = reconcileProbeSpend({
    chargeableShots: totals.chargeable_shots,
    chargeableUsdMicros: totals.chargeable_cost_usd_micros,
    unpricedShots: totals.unpriced_shots,
    charges: totals.charges,
    chargedUsdMicros: totals.charged_usd_micros,
    allocatedCharges: totals.allocated_charges,
    overflowCharges: totals.overflow_charges,
  });
  if (summed.status !== 'mismatch' && legs.some((leg) => leg.status === 'mismatch')) {
    return { ...summed, status: 'mismatch' };
  }
  return summed;
}

/**
 * Legs grouped by downstream SKU, biggest procurement spend first; legs
 * inside a SKU likewise.
 */
export function groupProbeSpend(legs: readonly ProbeSpendLeg[]): ProbeSpendSkuGroup[] {
  const buckets = new Map<string, ProbeSpendLeg[]>();
  for (const leg of legs) {
    const key = leg.sku_id ?? '';
    buckets.set(key, [...(buckets.get(key) ?? []), leg]);
  }
  const groups: ProbeSpendSkuGroup[] = [];
  for (const [key, members] of buckets) {
    const sorted = [...members].sort((left, right) =>
      right.procurement_cost_usd_micros - left.procurement_cost_usd_micros
      || right.shots - left.shots);
    const subtotal = subtotalOf(sorted);
    groups.push({
      key,
      skuId: key === '' ? null : key,
      skuName: sorted.find((leg) => leg.sku_name !== null)?.sku_name ?? null,
      legs: sorted,
      subtotal,
      verdict: totalsVerdict(subtotal, sorted),
    });
  }
  return groups.sort((left, right) =>
    right.subtotal.procurement_cost_usd_micros - left.subtotal.procurement_cost_usd_micros
    || right.subtotal.shots - left.subtotal.shots);
}

/** `¥` with 4 decimals from micro-CNY. */
export function formatCnyMicros(micros: number): string {
  return `¥${(micros / 1_000_000).toFixed(4)}`;
}
